/**
 * 外链舱富卡(2026-09-20)—— 首例 = 漫剧工坊 → drama.xbwhynot.com。
 *
 * ★老板 0920:「漫剧工坊的卡片风格还没有跟其他的统一,希望能突出一些,因为毕竟是个大功能」。
 *   统一 = 与同组 5 张 dept 卡共用 `WorkbenchCabinShell`(同高 / 同圆角 / 同信息层级 /
 *   同主角图处理 / 同 hover),不再是一眼可辨的轻量卡;
 *   突出 = 只用体系内已有的手段:独占的身份色档(chart=1,5 张 dept 卡没占的那一档)+
 *   外壳的 `feature` 档(把已有的 .wb-accent / .wb-mesh 各加深一档)。不加渐变、霓虹、动效。
 *
 * ★诚实三条(这张卡与 dept 卡真正的差别,全都写在卡面上):
 *   ① 它是**外站**:副标挂「独立站 ↗」标,CTA 是「前往独立站」+ ↗,aria-label 含「在新标签页打开」;
 *   ② **不报本站积分价**:catalog billable=0,价在对方站内 —— 卡脚那句讲的是「为什么这里没有价」,
 *      而不是编一个数;组件里根本不 import 价目表,想编也编不出来。
 *   ③ **没有真样图就不画样图**:dept 卡 hover 浮出的真样图来自本站产物,外链舱没有,
 *      那一格就空着 —— 主角图 + 价值主张 + 三条能力 chips 已经够用。
 */
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

import { capFigureOf } from "@/entities/scene-card/model/figures";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import type { CabinPresentation, CapabilityCard as CapabilityCardData } from "@/entities/capability";

import { WorkbenchCabinShell } from "./WorkbenchCabinShell";

interface Props {
  card: CapabilityCardData;
  pres: CabinPresentation;
  index: number;
  /** 落点由页面层给(统一走 `openExternalCabin`,新标签页 + noopener,noreferrer)。 */
  onOpen: () => void;
}

export function WorkbenchExternalCabinCard({ card, pres, index, onOpen }: Props) {
  const { t } = useTranslation();
  const skin = useActiveSkin();
  // 名称 / 简介仍以后端 catalog 为单源(i18n key 优先、回退后端中文)—— 运营改简介不发版。
  const title = t(`cabin.name.${card.capability}`, { defaultValue: card.displayName });
  const desc = t(`cabin.desc.${card.capability}`, { defaultValue: card.description ?? "" });
  const kind = pres.kindKey ? t(pres.kindKey) : null;

  return (
    <WorkbenchCabinShell
      cabinKey={card.capability}
      // 这张卡的数据仍来自 `/me/capabilities` 的一行,既有查询点(群页测试 / e2e)照旧能取到。
      legacyAttrName="data-capability-card"
      index={index}
      // chart 缺省回落 1:identityVar(0) 会退到 --primary,而 comic 皮肤的 --primary 是浅粉,
      // 压白底只有 3.1:1 —— 身份色至少要是 chart 档(comic chart-1 = 3.6:1,更深一点)。
      chart={pres.chart ?? 1}
      icon={pres.icon}
      title={title}
      tagline={
        <>
          {kind}
          {/* 「独立站」标走 CSS 类不走 Tailwind 任意值:身份色要带 alpha(`hsl(var(--ac)/.12)`),
              而 Tailwind 的任意值里那个 `/` 会被当成透明度修饰符的分隔符。同时**字用
              card-foreground 不用身份色** —— 身份色在 comic 皮肤压白底只有 3.6:1,当字不够 4.5。 */}
          <span className="wb-extbadge">
            <ExternalLink className="h-2.5 w-2.5" aria-hidden />
            {t("cabin.external.badge")}
          </span>
        </>
      }
      desc={desc}
      chips={pres.chipKeys?.map((k) => t(k))}
      // 单人方图(384×384),与 dept 卡的双人横图(512×341)构图不同 → solo 换构图锁。
      figure={{ url: capFigureOf(skin, card.imageKey ?? card.capability), solo: true }}
      footNote={pres.noteKey ? t(pres.noteKey) : null}
      footCta={t("cabin.cta.openExternal")}
      footCtaIcon={ExternalLink}
      feature
      ariaLabel={t("cabin.external.openAria", { name: title })}
      onOpen={onOpen}
    />
  );
}
